import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import CopyAllRounded from '@mui/icons-material/CopyAllRounded'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { Dialog, Box, Button, IconButton, Tooltip, useMediaQuery, useTheme, Typography } from '@mui/material'
import { Fragment, memo, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { DRUG_TABLE_COLUMN_WEIGHTS, PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import {
  autoSuggestDrugAdviceUsingDrug,
  DosageSelectMenu,
  DrugSelectMenu,
  DrugTypeSelectMenu,
  DurationSelectMenu,
  FrequencySelectMenu,
  handleAddTaperDrugAdviceRow,
  handleDrugAdviceChange,
  loadOptionsDrugs,
  NotesSelectMenu,
  nullAllValuesOfDrugAdvice,
  WhatTimeSelectMenu,
} from '../../PrescriptionMakingPageComponents'
import { PMPSingleRowBox, PMPSingleRowForDrugs } from '../../PrescriptionMakingPageStyles'
import { PRESCRIPTION_COLORS_MAP } from '../common/color-utils'
import PrescriptionPillViewInputComponent from '../common/PrescriptionPillViewInputComponent'
import DrugAdviceSelectedPillComponent from './components/DrugAdviceSelectedPillComponent'
import DrugAdviceContextForm from './DrugAdviceContextForm'
import DrugCreationForm from './DrugCreationForm'
import {
  getAndSetDrugWithRelatedNamesAndGenericDrugData,
  getAndSetDrugWithRelatedNamesForPillInput,
  getDrugAdviceEntitiesTitleToItemMap,
  getRenderDrugAdviceSubSectionsSet,
} from './utils'
import { buildRxPatientExplainerForRow, getRxExplainerLanguage } from './rxExplainer'

// Below this rendered width the 8-column drug table can no longer fit headers/values on a single
// line and collapses into vertical text (most visible in the chamber video split panel, where the
// form shares the screen with the call). At or under this width we switch to the stacked card
// layout instead. ~900px is roughly where the narrowest weight-1 columns (When/Frequency/Duration)
// stop wrapping their headers.
const COMPACT_LAYOUT_MAX_WIDTH = 900

const DrugAdviceInput = ({
  prescriptionViewSettings,
  drugsAdvice,
  setDrugsAdvice,
  handleAddDrugAdviceItem,
  handleDeleteDrugAdviceItem,
  clinicId,
  clinicBrandId,
  recommendedHealthEntityForDoctor,
  mentorProfileData,
  drugAdviceTypes,
  drugAdviceWhatTimeOptions,
  drugAdviceFrequencies,
  drugAdviceDurations,
  drugAdviceNotes,
  drugAdviceDosages,
  contextMenuSide,
  drugAdviceSubSections,
}) => {
  const recommendedDrugIds = recommendedHealthEntityForDoctor?.['drug-info']
  const convertedDataForPillUI = drugsAdvice?.map((item) => ({
    ...item?.drug,
    dosage: item?.dosage?.value,
    duration: item?.duration?.value,
    frequency: item?.frequency?.value,
    notes: item?.notes?.value,
    whatTime: item?.whatTime?.value,
    taperContinuation: item?.isTaperContinuation === true ? true : undefined,
    patientExplainerExcluded: item?.explainerExcluded === true ? true : undefined,
    __isNew__: item?.__isNew__,
  }))
  const [showDrugCreationDialog, setShowDrugCreationDialog] = useState(false)
  const [newDrugInputValue, setNewDrugInputValue] = useState('')
  const dosagesMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceDosages,
      prescriptionViewSettings,
    })
  }, [drugAdviceDosages, prescriptionViewSettings])

  const drugAdviceWhatTimeTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceWhatTimeOptions,
      prescriptionViewSettings,
    })
  }, [drugAdviceWhatTimeOptions, prescriptionViewSettings])
  const drugAdviceFrequenciesTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceFrequencies,
      prescriptionViewSettings,
    })
  }, [drugAdviceFrequencies, prescriptionViewSettings])
  const drugAdviceDurationsTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceDurations,
      prescriptionViewSettings,
    })
  }, [drugAdviceDurations, prescriptionViewSettings])
  const drugAdviceNotesTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceNotes,
      prescriptionViewSettings,
    })
  }, [drugAdviceNotes, prescriptionViewSettings])

  function onChangeForPillInput(valueData) {
    setDrugsAdvice((prev) => {
      let dataToWorkWIth = valueData
      if (typeof valueData === 'function') {
        dataToWorkWIth = valueData(
          prev.map((drug) => {
            return {
              ...drug?.drug,
              dosage: drug?.dosage?.value,
              duration: drug?.duration?.value,
              frequency: drug?.frequency?.value,
              notes: drug?.notes?.value,
              whatTime: drug?.whatTime?.value,
              __isNew__: drug?.__isNew__,
            }
          }),
        )
      }
      return dataToWorkWIth.map((item) => {
        const foundDosage = dosagesMap?.[item?.dosage] || { __isNew__: true }

        const foundWhatTime = drugAdviceWhatTimeTitleToItemMap?.[item?.whatTime] || {
          __isNew__: true,
        }
        const foundFrequency = drugAdviceFrequenciesTitleToItemMap?.[item?.frequency] || {
          __isNew__: true,
        }
        const foundDuration = drugAdviceDurationsTitleToItemMap?.[item?.duration] || {
          __isNew__: true,
        }
        const foundNotes = drugAdviceNotesTitleToItemMap?.[item?.notes] || { __isNew__: true }
        return {
          drug: item,
          dosage: { ...foundDosage, label: item?.dosage, value: item?.dosage },
          whatTime: { ...foundWhatTime, label: item?.whatTime, value: item?.whatTime },
          frequency: { ...foundFrequency, label: item?.frequency, value: item?.frequency },
          duration: { ...foundDuration, label: item?.duration, value: item?.duration },
          notes: { ...foundNotes, label: item?.notes, value: item?.notes },
          isTaperContinuation: item?.taperContinuation === true,
          explainerExcluded: item?.patientExplainerExcluded === true,
          __isNew__: item?.__isNew__,
        }
      })
    })
  }

  function handleSelectDrugAdviceItem(newValue, itemIdx) {

    if (newValue) {
      handleDrugAdviceChange('drug', itemIdx, newValue, setDrugsAdvice)
      autoSuggestDrugAdviceUsingDrug(itemIdx, newValue, setDrugsAdvice, drugAdviceTypes)
    } else {
      nullAllValuesOfDrugAdvice(itemIdx, setDrugsAdvice)
    }
  }

  function handleSelectDrugAdviceItemForPillInput(newValue, itemIdx) {
    onChangeForPillInput(newValue)
  }

  //for doc on select
  function handleChangeDrugItemForPillInput(allValues, itemIdx, newValue) {
    getAndSetDrugWithRelatedNamesForPillInput(
      mentorProfileData?.specializedCategories,
      newValue,
      allValues,
      clinicId,
      clinicBrandId,
      (val) => handleSelectDrugAdviceItemForPillInput(val),
    )
  }

  function closeDrugCreationDialog() {
    setShowDrugCreationDialog(false)
  }

  function createNewDrug(newValue) {
    setShowDrugCreationDialog(true)
    setNewDrugInputValue(newValue)
  }

  //for table drug select
  function handleChangeDrugItem(newValue, itemIdx, updateSelectedDrugStateOnly) {
    if (updateSelectedDrugStateOnly) {
      handleSelectDrugAdviceItem(newValue, itemIdx)
    } else {
      getAndSetDrugWithRelatedNamesAndGenericDrugData(
        mentorProfileData?.specializedCategories,
        newValue,
        clinicId,
        clinicBrandId,
        (val) => handleSelectDrugAdviceItem(val, itemIdx),
      )
    }
  }

  switch (prescriptionViewSettings?.prescriptionBuilderViewType) {
    case PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView:
      return (
        <>
          <PrescriptionPillViewInputComponent
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              recommendedDrugs: recommendedDrugIds,
            }}
            onChange={(valueArray, idx, newValue) => {
              handleChangeDrugItemForPillInput(valueArray, idx, newValue)
            }}
            onCreate={(inputValue) => {
              createNewDrug(inputValue)
            }}
            prescriptionViewSettings={prescriptionViewSettings}
            searchPlaceholder={'Search for drug'}
            debounceTimeout={200}
            getOptionLabel={(optn) => optn?.label}
            getOptionValue={(optn) => optn?.value}
            getOptionsResponse={loadOptionsDrugs}
            title="Rx"
            pillColor={PRESCRIPTION_COLORS_MAP.drugAdvice.color}
            highlightedPillColor={PRESCRIPTION_COLORS_MAP.drugAdvice.highlightColor}
            value={convertedDataForPillUI?.filter((item) => !!item?.title)}
            itemName={'drug'}
            ContextMenuFormComponent={DrugAdviceContextForm}
            contextMenuOpenSide={contextMenuSide}
            SelectedPillComponent={DrugAdviceSelectedPillComponent}
            showContextFormOnClickNewCondition={
              (item) => item?.__isNew__ || !item?.dosage || item?.duration || !item?.frequency
              // !item?.notes ||
              // !item?.whatTime
            }
            contextMenuAdditionalProps={{
              dosageOptions: drugAdviceDosages,
              durationOptions: drugAdviceDurations,
              frequencyOptions: drugAdviceFrequencies,
              whatTimeOptions: drugAdviceWhatTimeOptions,
              notesOptions: drugAdviceNotes,
              prescriptionViewSettings,
            }}
            useFullPageSearch={true}
          />
          <Dialog open={showDrugCreationDialog} onClose={closeDrugCreationDialog}>
            <DrugCreationForm
              inputString={newDrugInputValue}
              setDrugData={onChangeForPillInput}
              closeDialog={closeDrugCreationDialog}
            />
          </Dialog>
        </>
      )
    default:
      return (
        <DrugAdviceTableInput
          drugsAdvice={drugsAdvice}
          setDrugsAdvice={setDrugsAdvice}
          handleSelectDrugAdviceItem={handleChangeDrugItem}
          handleAddDrugAdviceItem={handleAddDrugAdviceItem}
          handleDeleteDrugAdviceItem={handleDeleteDrugAdviceItem}
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          recommendedDrugIds={recommendedDrugIds}
          mentorProfileData={mentorProfileData}
          drugAdviceTypes={drugAdviceTypes}
          drugAdviceWhatTimeOptions={drugAdviceWhatTimeOptions}
          drugAdviceFrequencies={drugAdviceFrequencies}
          drugAdviceDurations={drugAdviceDurations}
          drugAdviceNotes={drugAdviceNotes}
          drugAdviceDosages={drugAdviceDosages}
          prescriptionViewSettings={prescriptionViewSettings}
          drugAdviceSubSections={drugAdviceSubSections}
        />
      )
  }
}

function DrugAdviceTableInput({
  drugsAdvice,
  setDrugsAdvice,
  handleAddDrugAdviceItem,
  handleSelectDrugAdviceItem,
  handleDeleteDrugAdviceItem,
  clinicId,
  clinicBrandId,
  recommendedDrugIds,
  mentorProfileData,
  drugAdviceTypes,
  drugAdviceWhatTimeOptions,
  drugAdviceFrequencies,
  drugAdviceDurations,
  drugAdviceNotes,
  drugAdviceDosages,
  prescriptionViewSettings,
  drugAdviceSubSections,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // The prescription form often lives in a narrow split panel next to the video call (chamber
  // room), where the wide table squeezes its columns into unreadable vertical text. The viewport
  // can still be "desktop" sized, so a media query is not enough — measure the real rendered width
  // and fall back to the stacked card layout whenever the table would not have room to breathe.
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(null)
  useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return undefined
    const measure = () => setContainerWidth(node.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  // Real phones (isMobile) always use the cards; on desktop it depends on the measured panel width.
  const useCardLayout = isMobile || (containerWidth != null && containerWidth < COMPACT_LAYOUT_MAX_WIDTH)

  // Row numbers that skip taper-continuation rows (they belong to the medicine above)
  const rowNumberLabels = useMemo(() => {
    let counter = 0
    return (drugsAdvice || []).map((row) => {
      if (row?.isTaperContinuation) return '↳'
      counter += 1
      return `${counter}`
    })
  }, [drugsAdvice])

  // Patient explainer: one simple sentence per row in the prescription's language
  const explainerLanguage = getRxExplainerLanguage(prescriptionViewSettings)

  function getRowExplainerText(row, rowIndex) {
    if (!row?.drug?.label || row?.explainerExcluded) {
      return ''
    }
    const previousRow = rowIndex > 0 ? drugsAdvice?.[rowIndex - 1] : undefined
    return buildRxPatientExplainerForRow(row, explainerLanguage, previousRow)
  }

  function excludeRowExplainer(rowIndex) {
    setDrugsAdvice((prev) => {
      const prevArray = [...prev]
      prevArray[rowIndex] = { ...prevArray[rowIndex], explainerExcluded: true }
      return prevArray
    })
  }

  // "Copy to all" on the Duration column: apply this row's duration to every other
  // medicine row. An explicit action, so it also overwrites durations the practitioner
  // picked per-row (unlike the first-row default, which only fills empty/auto ones).
  const medicineRowCount = drugsAdvice?.filter((row) => row?.drug?.label)?.length || 0
  function showCopyDurationToAll(row) {
    return Boolean(row?.drug?.label && row?.duration?.label && medicineRowCount > 1)
  }
  function copyDurationToAllRows(sourceIndex) {
    setDrugsAdvice((prev) => {
      const sourceDuration = prev?.[sourceIndex]?.duration
      if (!sourceDuration?.label) return prev
      // Drop the auto-fill marker: these are now explicit choices that later
      // first-row defaulting must not overwrite.
      const { __autoFilledDuration, ...copiedDuration } = sourceDuration
      return prev.map((row, i) => {
        if (i === sourceIndex || !row?.drug?.label) return row
        return { ...row, duration: { ...copiedDuration } }
      })
    })
  }

  // Stacked card layout — used on real phones and whenever the form sits in a narrow panel (e.g.
  // the chamber video split view). One medicine per card, with every field full-size and always
  // visible: fields stay disabled (greyed) until a medicine is picked, then enable in place, so the
  // card never reflows and nothing jumps around as the row is filled.
  const compactCardLayout = (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative', // Create stacking context for dropdowns
          zIndex: 1,
        }}
      >
        {drugsAdvice?.map((drugAdviceRow, drugAdviceRowIndex) => {
          const hasDrug = Boolean(drugAdviceRow?.drug?.label)
          const isTaperContinuation = Boolean(drugAdviceRow?.isTaperContinuation)
          const rowExplainerText = getRowExplainerText(drugAdviceRow, drugAdviceRowIndex)

          return (
            <Box
              key={drugAdviceRowIndex}
              sx={{
                borderRadius: '14px',
                overflow: 'visible',
                ml: isTaperContinuation ? 3 : 0,
                background: '#ffffff',
                border: hasDrug
                  ? '1.5px solid rgba(67, 56, 202, 0.5)'
                  : '1px solid rgba(67, 56, 202, 0.15)',
                boxShadow: hasDrug
                  ? '0 2px 12px rgba(67, 56, 202, 0.10)'
                  : '0 1px 4px rgba(67, 56, 202, 0.05)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                position: 'relative',
                // Dropdowns portal to <body>, so earlier-card-over-later stacking is mostly
                // belt-and-braces; keeping it stable avoids any flicker as rows are added.
                zIndex: Math.max(1, (drugsAdvice?.length || 1) - drugAdviceRowIndex),
              }}
            >
              {/* Card Header: row number + medicine name only (actions live in the footer so the
                  medicine field gets the full width) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1.5,
                  borderBottom: hasDrug ? '1px solid rgba(67, 56, 202, 0.08)' : 'none',
                }}
              >
                {/* Drug Number Badge */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    background: hasDrug
                      ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
                      : 'rgba(148, 163, 184, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: hasDrug ? '#fff' : '#64748b',
                    fontWeight: 700,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {isTaperContinuation ? '↳' : rowNumberLabels[drugAdviceRowIndex]}
                </Box>

                {/* Drug Name Field - takes the full row so long generic names never crowd it */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('drug_name') && (
                    <DrugSelectMenu
                      clinicId={clinicId}
                      clinicBrandId={clinicBrandId}
                      selectedDrug={drugAdviceRow?.drug}
                      recommendedDrugs={recommendedDrugIds}
                      doctorSpecializations={mentorProfileData?.specializedCategories}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      setSelectedDrug={(newValue, updateSelectedDrugStateOnly) => {
                        handleSelectDrugAdviceItem(newValue, drugAdviceRowIndex, updateSelectedDrugStateOnly)
                      }}
                    />
                  )}
                </Box>

              </Box>

              {/* Details — only rendered once a medicine is chosen, so an empty card stays a single
                  clean row (just the medicine field). Picking a drug reveals these fields below it. */}
              {hasDrug && (
                <Box
                  sx={{
                    p: 1.5,
                    pt: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {/* Row 1: Type + Dosage */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('type') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'type')?.title || 'Type'}
                        color="#8b5cf6"
                      >
                        <DrugTypeSelectMenu
                          allowEnteringValue={hasDrug}
                          selectedType={drugAdviceRow?.drugType}
                          options={drugAdviceTypes}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedType={(newValue) =>
                            handleDrugAdviceChange(
                              'drugType',
                              drugAdviceRowIndex,
                              newValue,
                              setDrugsAdvice,
                              drugAdviceTypes,
                            )
                          }
                        />
                      </MobileFieldWrapper>
                    )}
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('dosage') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'dosage')?.title || 'Dosage'}
                        color="#4338ca"
                      >
                        <DosageSelectMenu
                          allowEnteringValue={hasDrug}
                          selectedDosage={drugAdviceRow?.dosage}
                          options={drugAdviceDosages}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedDosage={(newValue) =>
                            handleDrugAdviceChange('dosage', drugAdviceRowIndex, newValue, setDrugsAdvice)
                          }
                        />
                      </MobileFieldWrapper>
                    )}
                  </Box>

                  {/* Row 2: When + Frequency */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('when') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'when')?.title || 'When'}
                        color="#f59e0b"
                      >
                        <WhatTimeSelectMenu
                          allowEnteringValue={hasDrug}
                          selectedWhatTime={drugAdviceRow?.whatTime}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedWhatTime={(newValue) =>
                            handleDrugAdviceChange('whatTime', drugAdviceRowIndex, newValue, setDrugsAdvice)
                          }
                          options={drugAdviceWhatTimeOptions}
                        />
                      </MobileFieldWrapper>
                    )}
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('frequency') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'frequency')?.title || 'Frequency'}
                        color="#22c55e"
                      >
                        <FrequencySelectMenu
                          allowEnteringValue={hasDrug}
                          selectedFrequency={drugAdviceRow?.frequency}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedFrequency={(newValue) =>
                            handleDrugAdviceChange('frequency', drugAdviceRowIndex, newValue, setDrugsAdvice)
                          }
                          options={drugAdviceFrequencies}
                        />
                      </MobileFieldWrapper>
                    )}
                  </Box>

                  {/* Row 3: Duration */}
                  {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('duration') && (
                    <MobileFieldWrapper
                      label={drugAdviceSubSections?.find((item) => item?.uid === 'duration')?.title || 'Duration'}
                      color="#ec4899"
                      fullWidth
                      labelAction={
                        showCopyDurationToAll(drugAdviceRow) ? (
                          <Button
                            onClick={() => copyDurationToAllRows(drugAdviceRowIndex)}
                            size="small"
                            startIcon={<CopyAllRounded sx={{ fontSize: '14px !important' }} />}
                            sx={{
                              textTransform: 'none',
                              color: '#ec4899',
                              fontWeight: 600,
                              fontSize: 11,
                              lineHeight: 1,
                              minWidth: 0,
                              p: '2px 6px',
                              '&:hover': { backgroundColor: 'rgba(236, 72, 153, 0.08)' },
                            }}
                          >
                            Copy to all
                          </Button>
                        ) : null
                      }
                    >
                      <DurationSelectMenu
                        allowEnteringValue={hasDrug}
                        selectedDurationTime={drugAdviceRow?.duration}
                        prescriptionViewSettings={prescriptionViewSettings}
                        menuPortalTarget={document.body}
                        setSelectedDurationTime={(newValue) =>
                          handleDrugAdviceChange('duration', drugAdviceRowIndex, newValue, setDrugsAdvice)
                        }
                        options={drugAdviceDurations}
                      />
                    </MobileFieldWrapper>
                  )}

                  {/* Row 4: Notes */}
                  {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('notes') && (
                    <MobileFieldWrapper
                      label={drugAdviceSubSections?.find((item) => item?.uid === 'notes')?.title || 'Notes'}
                      color="#64748b"
                      fullWidth
                    >
                      <NotesSelectMenu
                        idx={drugAdviceRowIndex}
                        allowEnteringValue={hasDrug}
                        selectedNote={drugAdviceRow?.notes}
                        prescriptionViewSettings={prescriptionViewSettings}
                        isDrugNew={drugAdviceRow?.drug?.__isNew__}
                        menuPortalTarget={document.body}
                        setSelectedNote={(newValue) =>
                          handleDrugAdviceChange('notes', drugAdviceRowIndex, newValue, setDrugsAdvice)
                        }
                        options={drugAdviceNotes}
                      />
                    </MobileFieldWrapper>
                  )}

                  {/* patient explainer — quiet helper line the practitioner can dismiss */}
                  {rowExplainerText && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '11px',
                        fontStyle: 'italic',
                        color: '#64748b',
                        background: 'rgba(67, 56, 202, 0.04)',
                        borderRadius: '8px',
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <span style={{ minWidth: 0, flex: 1 }}>{rowExplainerText}</span>
                      <IconButton
                        onClick={() => excludeRowExplainer(drugAdviceRowIndex)}
                        size="small"
                        sx={{ p: 0.25, color: '#94a3b8', flexShrink: 0 }}
                      >
                        <CloseIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}

              {/* Card footer: tapering + delete, kept out of the header so the medicine name field
                  spans the full width. "Add tapering dosage" only shows once a medicine is chosen. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  px: 1.5,
                  py: 1,
                  borderTop: hasDrug ? '1px solid rgba(67, 56, 202, 0.08)' : 'none',
                }}
              >
                {hasDrug && (
                  <Tooltip title="Add a follow-up row for this medicine (e.g. a tapering dose)" arrow>
                    <Button
                      onClick={() => handleAddTaperDrugAdviceRow(drugAdviceRowIndex, setDrugsAdvice)}
                      startIcon={<AddCircleOutline />}
                      size="small"
                      sx={{
                        textTransform: 'none',
                        color: '#4338ca',
                        fontWeight: 600,
                        fontSize: 13,
                        px: 1,
                        '&:hover': { backgroundColor: 'rgba(67, 56, 202, 0.08)' },
                      }}
                    >
                      Add tapering dosage
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Delete medicine" arrow>
                  <IconButton
                    onClick={() => handleDeleteDrugAdviceItem(drugAdviceRowIndex, setDrugsAdvice)}
                    size="small"
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' },
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )
        })}

        {/* Add New Prescription Button */}
        <Box
          onClick={() => handleAddDrugAdviceItem(setDrugsAdvice)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            p: 2.5,
            borderRadius: '16px',
            border: '2px dashed rgba(67, 56, 202, 0.3)',
            background: 'linear-gradient(145deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.04) 100%)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'rgba(67, 56, 202, 0.5)',
              background: 'linear-gradient(145deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.08) 100%)',
              transform: 'scale(1.01)',
            },
            '&:active': {
              transform: 'scale(0.99)',
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(67, 56, 202, 0.25)',
            }}
          >
            <AddCircleOutline sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography
            sx={{
              color: '#4338ca',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Add Medication
          </Typography>
        </Box>
      </Box>
  )

  // Desktop Table View — used only when the panel is wide enough for all columns to breathe.
  const tableLayout = (
    <Box
      sx={{
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(67, 56, 202, 0.12)',
        background: 'white',
        boxShadow: '0 2px 8px rgba(67, 56, 202, 0.06)',
        overflowX: 'auto',
        colorScheme: 'light', // Force light mode to prevent dark mode inheritance
        color: '#1f2937', // Explicit dark text color to ensure visibility
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(67, 56, 202, 0.05)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(67, 56, 202, 0.2)',
          borderRadius: '10px',
          '&:hover': {
            background: 'rgba(67, 56, 202, 0.3)',
          },
        },
      }}
    >
      {/* default row */}
      <PMPSingleRowForDrugs isHeaderRow useGreyBorders>
        <PMPSingleRowBox style={{ width: '3%' }} centered borderRight isHeaderRow>
          #
        </PMPSingleRowBox>
        {/* medicine first, then type — rendering order here decides column order */}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('drug_name') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'drug_name',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'drug_name')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('type') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'type',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'type')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('dosage') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'dosage',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'dosage')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('when') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'when',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'when')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('frequency') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'frequency',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'frequency')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('duration') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'duration',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'duration')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('notes') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'notes',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'notes')?.title}
          </PMPSingleRowBox>
        )}
        <div style={{ width: '3%' }}></div>
      </PMPSingleRowForDrugs>

      {/* to be rendered a number of times */}
      {drugsAdvice?.map((drugAdviceRow, drugAdviceRowIndex) => {
        const rowExplainerText = getRowExplainerText(drugAdviceRow, drugAdviceRowIndex)
        const hasMedicineForRow = Boolean(drugAdviceRow?.drug?.label)
        return (
        <Fragment key={drugAdviceRowIndex}>
        <PMPSingleRowForDrugs
          useGreyBorders
          isLastRow={drugAdviceRowIndex + 1 === drugsAdvice?.length && !rowExplainerText}
          style={
            drugAdviceRow?.isTaperContinuation
              ? { background: 'rgba(67, 56, 202, 0.04)', boxShadow: 'inset 3px 0 0 #4338ca' }
              : undefined
          }
        >
          {/* medicine first, then type — rendering order here decides column order */}
          <PMPSingleRowBox style={{ width: '3%' }} centered>
            {drugAdviceRow?.isTaperContinuation ? '↳' : rowNumberLabels[drugAdviceRowIndex]}
          </PMPSingleRowBox>

          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('drug_name') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'drug_name',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
                position: 'relative',
                zIndex: 20, // For dropdown, but GenericDrugComponent inside has lower z-index
              }}
            >
              <DrugSelectMenu
                clinicId={clinicId}
                clinicBrandId={clinicBrandId}
                selectedDrug={drugAdviceRow?.drug}
                recommendedDrugs={recommendedDrugIds}
                doctorSpecializations={mentorProfileData?.specializedCategories}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedDrug={(newValue, updateSelectedDrugStateOnly) => {
                  handleSelectDrugAdviceItem(
                    newValue,
                    drugAdviceRowIndex,
                    updateSelectedDrugStateOnly,
                  )
                }}
              />
              {/* // this is expanding full width, and making the flexgrow container expand too, i need flexGrow to be respoected and it should impose a max width on children like bbelow */}
            </div>
          )}

          {/* until a medicine is picked, keep the rest of the row quiet so the
              practitioner can focus on the Medicine column */}
          {!hasMedicineForRow && (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px',
                fontSize: '11.5px',
                fontStyle: 'italic',
                color: '#b6bcc8',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                userSelect: 'none',
              }}
            >
              Pick a medicine — type, dose, timing, frequency, duration &amp; notes unlock here
            </div>
          )}

          {hasMedicineForRow && getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('type') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'type',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <div style={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}>
                <DrugTypeSelectMenu
                  allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                  selectedType={drugAdviceRow?.drugType}
                  options={drugAdviceTypes}
                  prescriptionViewSettings={prescriptionViewSettings}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  setSelectedType={(newValue) =>
                    handleDrugAdviceChange(
                      'drugType',
                      drugAdviceRowIndex,
                      newValue,
                      setDrugsAdvice,
                      drugAdviceTypes,
                    )
                  }
                />
              </div>
              {Boolean(drugAdviceRow?.drug?.label) && (
                <Tooltip title="Add follow-up row for this medicine (e.g. tapering)" arrow>
                  <IconButton
                    onClick={() => handleAddTaperDrugAdviceRow(drugAdviceRowIndex, setDrugsAdvice)}
                    size="small"
                    sx={{
                      color: '#4338ca',
                      p: 0.125,
                      ml: -0.25,
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.1)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AddCircleOutline sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}

          {hasMedicineForRow && getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('dosage') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'dosage',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
              }}
            >
              <DosageSelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedDosage={drugAdviceRow?.dosage}
                options={drugAdviceDosages}
                prescriptionViewSettings={prescriptionViewSettings}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedDosage={(newValue) =>
                  handleDrugAdviceChange('dosage', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
              />
            </div>
          )}

          {hasMedicineForRow && getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('when') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'when',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
              }}
            >
              <WhatTimeSelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedWhatTime={drugAdviceRow?.whatTime}
                prescriptionViewSettings={prescriptionViewSettings}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedWhatTime={(newValue) =>
                  handleDrugAdviceChange('whatTime', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceWhatTimeOptions}
              />
            </div>
          )}
          {hasMedicineForRow && getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('frequency') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'frequency',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
                position: 'relative',
                zIndex: 25, // Higher than Generic Name component (zIndex: 1) to appear in front
              }}
            >
              <FrequencySelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedFrequency={drugAdviceRow?.frequency}
                prescriptionViewSettings={prescriptionViewSettings}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedFrequency={(newValue) =>
                  handleDrugAdviceChange('frequency', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceFrequencies}
              />
            </div>
          )}
          {hasMedicineForRow && getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('duration') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'duration',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <div style={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}>
                <DurationSelectMenu
                  allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                  selectedDurationTime={drugAdviceRow?.duration}
                  prescriptionViewSettings={prescriptionViewSettings}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  setSelectedDurationTime={(newValue) =>
                    handleDrugAdviceChange('duration', drugAdviceRowIndex, newValue, setDrugsAdvice)
                  }
                  options={drugAdviceDurations}
                />
              </div>
              {showCopyDurationToAll(drugAdviceRow) && (
                <Tooltip title="Copy this duration to all medicines" arrow>
                  <IconButton
                    onClick={() => copyDurationToAllRows(drugAdviceRowIndex)}
                    size="small"
                    sx={{
                      color: '#4338ca',
                      p: 0.125,
                      ml: -0.25,
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.1)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <CopyAllRounded sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}
          {hasMedicineForRow && getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('notes') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'notes',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                minWidth: 0,
              }}
            >
              <NotesSelectMenu
                idx={drugAdviceRowIndex}
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedNote={drugAdviceRow?.notes}
                prescriptionViewSettings={prescriptionViewSettings}
                isDrugNew={drugAdviceRow?.drug?.__isNew__}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedNote={(newValue) =>
                  handleDrugAdviceChange('notes', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceNotes}
              />
            </div>
          )}
          {hasMedicineForRow && (
          <PMPSingleRowBox style={{ width: '3%' }} whiteBG centered>
            <Tooltip title="Delete prescription" arrow>
              <IconButton
                onClick={() => handleDeleteDrugAdviceItem(drugAdviceRowIndex, setDrugsAdvice)}
                size="small"
                sx={{
                  color: '#ef4444',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DeleteOutline sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </PMPSingleRowBox>
          )}
        </PMPSingleRowForDrugs>
        {/* patient explainer — quiet helper line the practitioner can dismiss */}
        {rowExplainerText && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '2px 8px 4px 3%',
              fontSize: '11px',
              fontStyle: 'italic',
              color: '#64748b',
              background: 'rgba(67, 56, 202, 0.025)',
              borderBottom:
                drugAdviceRowIndex + 1 === drugsAdvice?.length
                  ? 'none'
                  : '1px solid rgba(67, 56, 202, 0.08)',
            }}
          >
            <span style={{ minWidth: 0 }}>{rowExplainerText}</span>
            <Tooltip title="Remove this explanation from the prescription" arrow>
              <IconButton
                onClick={() => excludeRowExplainer(drugAdviceRowIndex)}
                size="small"
                sx={{ p: 0.25, color: '#94a3b8', flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </div>
        )}
        </Fragment>
        )
      })}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
          borderTop: '1px solid rgba(67, 56, 202, 0.1)',
          background: 'rgba(67, 56, 202, 0.02)',
        }}
      >
        <Tooltip title="Add new prescription" arrow>
          <IconButton
            onClick={() => handleAddDrugAdviceItem(setDrugsAdvice)}
            sx={{
              color: '#4338ca',
              '&:hover': {
                backgroundColor: 'rgba(67, 56, 202, 0.1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <AddCircleOutline sx={{ fontSize: 28 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  // The wrapper is always rendered (in both layouts) so its width can be measured to decide which
  // layout to show. width:100% makes it report the panel's available width regardless of contents.
  // On desktop we hold off one synchronous pass until the width is measured (the useLayoutEffect
  // runs before paint, so nothing visibly flashes) rather than mounting the table only to swap it
  // out a moment later. On mobile the choice is known up-front, so render immediately.
  const layoutReady = isMobile || containerWidth != null
  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {layoutReady ? (useCardLayout ? compactCardLayout : tableLayout) : null}
    </div>
  )
}

// Field wrapper for the stacked card layout: a coloured label above a bordered dropdown. Two of
// these sit side-by-side (each ~half width) unless `fullWidth`, which spans the whole card row.
// `labelAction` renders right-aligned in the label row (e.g. Duration's "Copy to all").
const MobileFieldWrapper = ({ label, color, fullWidth, labelAction, children }) => (
  <Box
    sx={{
      flex: fullWidth ? '1 1 100%' : '1 1 calc(50% - 6px)',
      minWidth: fullWidth ? '100%' : 0,
      position: 'relative',
    }}
  >
    <Box
      component="div"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: color,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      {label}
      {labelAction && <Box sx={{ ml: 'auto' }}>{labelAction}</Box>}
    </Box>
    <Box
      sx={{
        borderRadius: '10px',
        overflow: 'visible',
        border: `1px solid ${color}20`,
        background: '#fff',
        transition: 'all 0.2s ease',
        '&:focus-within': {
          borderColor: color,
          boxShadow: `0 0 0 2px ${color}15`,
        },
        // Ensure dropdowns appear above other elements
        '& .css-1nmdiq5-menu, & [class*="-menu"]': {
          zIndex: 9999,
        },
      }}
    >
      {children}
    </Box>
  </Box>
)

export default memo(DrugAdviceInput)

export function getPercentageOfItemForDrugTableCellWidth(
  sectionId,
  availableSpaceInPercentage,
  setOfVisibleItems,
) {
  const weightForThisSection = DRUG_TABLE_COLUMN_WEIGHTS?.[sectionId]

  const weightSum = Object.entries(DRUG_TABLE_COLUMN_WEIGHTS)
    .filter(([key, value]) => setOfVisibleItems.has(key))
    .reduce((acc, [key, value]) => acc + value, 0)
  return `${(weightForThisSection / weightSum) * availableSpaceInPercentage}%`
}
